// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { findStep } from "@pacific/App/Widgets/CheckoutStepper/CheckoutStepper.helper";
import { StepperContext } from "@pacific/App/Widgets/CheckoutStepper/StepperContext";
import Check from "@pacific/XDL/Icons/Check";
import { headingsFontConfig } from "@pacific/XDL/PreStyle/typography";
import { xBlueprintName } from "@pacific/XDL/types";
import { color, xScreen } from "@pacific/XDL/Utilities";
import React, { useContext } from "react";
import { css } from "styled-components";

interface OwnProps {}

type Props = OwnProps;

export interface CheckoutStepperStyles {
    container?: InjectableCss;
    stepperWrapper?: InjectableCss;
    stepperItem: {
        wrapper?: InjectableCss;
        insideWrapper?: InjectableCss;
        stepNumber?: InjectableCss;
        stepIcon?: IconPresentationProps;
        title?: TypographyProps;
    };
}
const xsBreakpoint = 575;
export const styles: CheckoutStepperStyles = {
    container: {
        css: css`
            max-width: 524px;
            margin-bottom: 24px;
            ${xScreen("lte", "md")} {
                &&& {
                    margin-bottom: 16px;
                }
            }
        `,
    },
    stepperWrapper: {
        css: css`
            display: flex;
            width: 100%;
            align-items: flex-start;

            & > li:last-child:after {
                display: none;
            }
        `,
    },
    stepperItem: {
        wrapper: {
            css: css`
                display: flex;
                justify-content: flex-start;
                position: relative;
                cursor: pointer;
                &:after {
                    content: "";
                    height: 1px;
                    width: 48px;
                    background: ${color("common.border")};
                    top: 28px;
                    position: relative;
                    transform: translateY(-14px);
                    margin: 0 8px;
                }
            `,
        },
        insideWrapper: {
            css: css`
                display: flex;
                z-index: 10;
                align-items: center;
                flex-direction: row;
            `,
        },
        stepNumber: {
            css: css`
                display: flex;
                align-items: center;
                justify-content: center;
                width: 24px;
                height: 24px;
                background: ${color("common.ui5")};
                font-size: 14px;
                font-weight: bold;
                letter-spacing: 0;
                line-height: 19px;
                font-family: ${headingsFontConfig[BLUEPRINT_NAME as xBlueprintName].fontFamily};
                color: ${color("common.ui1")};

                ${({ isCompleted, isActive }: any) => {
                    if (isCompleted || isActive) {
                        return css`
                            background: ${isCompleted ? color("common.ui9") : color("common.ui8")};
                        `;
                    }
                }}
            `,
        },
        stepIcon: {
            src: Check,
            size: 12,
            color: "common.ui1",
        },
        title: {
            size: 12,
            weight: 600,
            css: css`
                letter-spacing: 0;
                line-height: 18px;
                color: ${color("common.ui6")};
                margin-left: 8px;
                ${xScreen("lte", "md")} {
                    display: none;
                }
            `,
        },
    },
};

export const CheckoutStepper: React.FC<Props> = () => {
    const { steps, currentStep, maxStep, moveToStepHandler } = useContext(StepperContext);
    const currentStepText = findStep(steps, currentStep).name;

    const StepperComponent = () => {
        const style = styles.stepperItem;
        return (
            <Typography as="ul" {...styles.stepperWrapper}>
                {steps.map((item, index) => {
                    const isCompleted = item.index < currentStep;
                    const isActive = item.index === currentStep;
                    const isClickable = item.index <= maxStep;

                    const props = { isCompleted, isActive, isClickable };
                    const className = `${isActive ? "active" : ""} ${isCompleted ? "completed" : ""} ${
                        isClickable ? "clickable" : ""
                    }`.trim();

                    return (
                        <Typography
                            as="li"
                            {...style.wrapper}
                            {...props}
                            key={item.name}
                            className={className}
                            onClick={() => isClickable && moveToStepHandler(item.index)}
                        >
                            <StyledWrapper {...style.insideWrapper} {...props}>
                                <Typography {...style.stepNumber} {...props}>
                                    {isCompleted ? <Icon {...style.stepIcon} /> : index + 1}
                                </Typography>
                                <Typography {...style.title} {...props}>
                                    {item.name}
                                </Typography>
                            </StyledWrapper>
                        </Typography>
                    );
                })}
            </Typography>
        );
    };

    return (
        <StyledWrapper {...styles.container}>
            <VisuallyHidden aria-current="step">{translate(currentStepText)}</VisuallyHidden>
            <StepperComponent />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: CheckoutStepper,
    definition: {
        group: "Checkout - Shipping",
        allowedContexts: ["CheckoutShippingPage", "CheckoutReviewAndSubmitPage"],
        displayName: "Checkout Stepper",
        fieldDefinitions: [],
    },
};

export default widgetModule;
